import { z } from 'zod';
import type { UserId } from '@/shared/types/common.types';

/** Identity roles accepted by the web client, including the service's seeded roles. */
export const USER_ROLES = ['SuperAdmin', 'Admin', 'Manager', 'Engineer', 'User', 'Guest'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_TYPES = ['COMPANY', 'EXTERNAL', 'GUEST'] as const;
export type UserType = (typeof USER_TYPES)[number];

/**
 * Authenticated user profile.
 *
 * Normalized user profile returned by the IdentityServer4 authentication REST API.
 */
export interface AuthenticatedUser {
  readonly id: UserId;
  readonly username: string;
  readonly email: string;
  readonly emailVerified: boolean;
  readonly givenName: string;
  readonly familyName: string;
  readonly middleName?: string;
  readonly phoneNumber?: string;
  readonly phoneNumberVerified: boolean;
  readonly picture?: string;
  readonly organization?: string;
  readonly department?: string;
  readonly employeeId?: string;
  readonly userType?: UserType;
  readonly roles: readonly UserRole[];
  readonly locale?: string;
  readonly zoneInfo?: string;
  readonly updatedAt?: string;
}

/** Session-user fields that the profile editor can update. */
export interface EditableUserDetails {
  readonly givenName: string;
  readonly familyName: string;
  readonly phoneNumber: string;
  readonly department: string;
}

/** In-memory access session issued by the IdentityServer4 authentication REST API. */
export interface AuthSession {
  readonly accessToken: string;
  /** Absolute expiry as epoch milliseconds, derived from `expires_in` at issue time. */
  readonly expiresAt: number;
  readonly tokenType: string;
  /** Kept in memory with the access token; the server cookie restores it after a reload. */
  readonly user: AuthenticatedUser;
}

export interface LoginCredentials {
  readonly username: string;
  readonly password: string;
  readonly rememberMe: boolean;
}

export const authenticationUserSchema = z.object({
  id: z.string().min(1),
  username: z.string().min(1),
  email: z.string(),
  givenName: z.string(),
  familyName: z.string(),
  role: z.string(),
  department: z.string().optional(),
  employeeId: z.string().optional(),
  organization: z.string().optional(),
  emailVerified: z.boolean().optional(),
  phoneNumber: z.string().optional(),
  phoneNumberVerified: z.boolean().optional(),
  picture: z.string().optional(),
  locale: z.string().optional(),
  zoneInfo: z.string().optional(),
  updatedAt: z.string().optional(),
  middleName: z.string().optional(),
});

export const authenticationResponseSchema = z.object({
  isSuccess: z.boolean(),
  error: z.string().nullable().optional(),
  accessToken: z.string().nullable().optional(),
  expiresIn: z.number().int().positive().optional(),
  tokenType: z.string().nullable().optional(),
  user: authenticationUserSchema.nullable().optional(),
});

export type AuthenticationResponse = z.infer<typeof authenticationResponseSchema>;

export interface AuthState {
  readonly user: AuthenticatedUser | null;
  readonly isAuthenticated: boolean;
  readonly isRestoringSession: boolean;
  readonly isSigningIn: boolean;
  readonly error: string | null;
}
